import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Crown, UserPlus, X } from "lucide-react";
import { useMemo, useState } from "react";

import { ApiError, api } from "../api/client";
import type { ExerciseAccessView, UnitShare } from "../api/types";
import { Callout } from "../components/Callout";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Dialog, DialogBody } from "../components/Dialog";
import { Motion, Presence } from "../components/presence";
import { Select } from "../components/Select";
import { Badge, Button, IconButton, inputClass, SectionLabel, Segmented } from "../components/ui";
import { t, tServer } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { Waiting } from "../components/Waiting";

/** Fenêtre « Partager » d'un dossier (docs/17 §3.1, §3.2, §8.3, lot 5) : propriétaire et « Transférer la
 *  propriété… », toute l'unité (Aucun accès · Lecture seule · Modification), comptes de l'unité un à un, note sur
 *  les deux niveaux. Le partage s'enregistre hors du suivi de version du dossier : ceux qui l'éditent ne reçoivent
 *  aucun 412. Seul le propriétaire le règle ; les autres comptes le voient sans pouvoir le changer. Un dossier de
 *  l'unité (sans propriétaire) reste modifiable par tous : un administrateur peut lui attribuer un propriétaire. */

type Level = "reader" | "editor";
interface Member {
  account_id: string;
  name: string;
  appointment: string | null;
  level: Level;
  /** Faux pour un compte désactivé depuis : sa ligne reste (docs/17 §5.6), renvoyée telle quelle à l'enregistrement
   *  tant que le propriétaire ne la retire pas. */
  active?: boolean;
}

/** Corps du `PUT /access` d'après la saisie. */
export function accessBody(unit: UnitShare, members: Member[]): { unit: UnitShare; members: { account_id: string; level: Level }[] } {
  return { unit, members: members.map((m) => ({ account_id: m.account_id, level: m.level })) };
}

/** La saisie diffère-t-elle du partage enregistré ? */
export function accessChanged(view: ExerciseAccessView | undefined, unit: UnitShare, members: Member[]): boolean {
  if (!view) return false;
  if (view.unit !== unit || view.members.length !== members.length) return true;
  const saved = new Map(view.members.map((m) => [m.account_id, m.level]));
  return members.some((m) => saved.get(m.account_id) !== m.level);
}

export function ShareDialog({ exerciseId, name, onClose }: { exerciseId: string; name: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const view = useQuery({ queryKey: ["exercise-access", exerciseId], queryFn: () => api.sharing.access(exerciseId) });
  const directory = useQuery({ queryKey: ["accounts-directory"], queryFn: api.accounts.directory, staleTime: 60_000 });
  // Saisie : partie de l'enregistré, gardée tant que la fenêtre est ouverte.
  const [edit, setEdit] = useState<{ unit: UnitShare; members: Member[] } | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [heir, setHeir] = useState("");
  const [confirming, setConfirming] = useState(false);
  const data = view.data;
  const unit = edit?.unit ?? data?.unit ?? "none";
  const members = useMemo<Member[]>(
    () => edit?.members ?? (data?.members ?? []).map((m) => ({
        account_id: m.account_id,
        name: m.name,
        appointment: m.appointment,
        level: m.level,
        active: m.active,
      })),
    [edit, data],
  );
  const canShare = Boolean(data?.can_share);
  const canTransfer = data?.level === "owner";
  const dirty = accessChanged(data, unit, members);
  const ownerId = data?.owner?.id ?? null;
  const addable = (directory.data ?? []).filter((a) => a.id !== ownerId && !members.some((m) => m.account_id === a.id));
  const heirs = (directory.data ?? []).filter((a) => a.id !== ownerId);
  const heirName = heirs.find((a) => a.id === heir)?.display_name ?? "";

  const refresh = (next: ExerciseAccessView) => {
    queryClient.setQueryData(["exercise-access", exerciseId], next);
    void queryClient.invalidateQueries({ queryKey: ["exercises"] });
  };
  const save = useMutation({
    mutationFn: () => api.sharing.save(exerciseId, accessBody(unit, members)),
    onSuccess: (next) => {
      refresh(next);
      setEdit(null);
      notify({ text: t("Partage enregistré"), tone: "ok" });
      onClose();
    },
  });
  const transfer = useMutation({
    mutationFn: () => api.sharing.transfer(exerciseId, heir),
    onSuccess: (next) => {
      refresh(next);
      setConfirming(false);
      setTransferring(false);
      setEdit(null);
      notify({ text: t("Propriétaire changé"), detail: t("{nom} est propriétaire de « {dossier} ».", { nom: heirName, dossier: name }), tone: "ok" });
    },
    onError: () => setConfirming(false),
  });

  const change = (next: Partial<{ unit: UnitShare; members: Member[] }>) => setEdit({ unit, members, ...next });
  const failure = save.error ?? transfer.error;
  const unitOptions = [
    { value: "none" as const, label: t("Aucun accès") },
    { value: "reader" as const, label: t("Lecture seule") },
    { value: "editor" as const, label: t("Modification") },
  ];
  const levelOptions = [
    { value: "reader" as const, label: t("Lecture seule") },
    { value: "editor" as const, label: t("Modification") },
  ];

  return (
    <>
      <Dialog
        title={t("Partager « {dossier} »", { dossier: name })}
        onClose={onClose}
        dirty={dirty}
        width={560}
        footer={
          canShare ? (
            <>
              <Button variant="ghost" onClick={onClose}>
                {t("Annuler")}
              </Button>
              <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()} data-attention="share-save">
                {save.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Enregistrer")}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              {t("Fermer")}
            </Button>
          )
        }
      >
        <DialogBody>
          {view.isLoading && <p className="text-ui text-ink-muted">{<Waiting>{t("Chargement…")}</Waiting>}</p>}
          {view.isError && <Callout tone="danger">{t("Partage illisible pour l'instant : réessayez.")}</Callout>}
          {data && (
            <>
              <div className="flex min-h-8 flex-wrap items-center gap-x-2 gap-y-1 py-1.5">
                <Crown size={14} className="shrink-0 text-ink-muted" />
                <span className="text-ui text-ink">
                  {data.owner ? t("Propriétaire : {nom}", { nom: data.owner.name }) : t("Dossier de l'unité")}
                </span>
                {canTransfer && (
                  <Button variant="link" onClick={() => setTransferring((v) => !v)} aria-expanded={transferring}>
                    {data.owner ? t("Transférer la propriété…") : t("Attribuer un propriétaire…")}
                  </Button>
                )}
              </div>
              <Presence show={transferring}>
                {transferring && (
                  <Motion origin="top" className="flex flex-wrap items-center gap-2 rounded-lg bg-canvas px-3 py-2.5">
                    <Select
                      className={inputClass}
                      value={heir}
                      onChange={(e) => setHeir(e.target.value)}
                      placeholder={t("Choisir un compte…")}
                      aria-label={t("Nouveau propriétaire")}
                    >
                      {heirs.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.appointment ? `${a.display_name} · ${a.appointment}` : a.display_name}
                        </option>
                      ))}
                    </Select>
                    <Button
                      variant="secondary"
                      blocked={!heir}
                      onBlocked={() => notify({ text: t("Choisissez d'abord le nouveau propriétaire"), tone: "info" })}
                      onClick={() => setConfirming(true)}
                    >
                      {t("Transférer")}
                    </Button>
                  </Motion>
                )}
              </Presence>

              {data.unit_item ? (
                <Callout tone="info">
                  {t("Dossier de l'unité : il reste modifiable par toute l'unité. Attribuez-lui d'abord un propriétaire pour en régler le partage.")}
                </Callout>
              ) : (
                <>
                  <div className="space-y-1.5 border-t border-line pt-3">
                    <SectionLabel>{t("Toute l'unité")}</SectionLabel>
                    {canShare ? (
                      <Segmented
                        ariaLabel={t("Toute l'unité")}
                        options={unitOptions}
                        value={unit}
                        onChange={(value) => change({ unit: value })}
                      />
                    ) : (
                      <p className="text-ui text-ink">{unitOptions.find((o) => o.value === unit)?.label}</p>
                    )}
                  </div>
                  <div className="space-y-1.5 border-t border-line pt-3">
                    <SectionLabel>{t("Comptes")}</SectionLabel>
                    {members.length === 0 && <p className="text-ui text-ink-muted">{t("Partagé avec aucun compte.")}</p>}
                    <ul className="divide-y divide-line">
                      {members.map((m) => (
                        <li key={m.account_id} className="flex min-h-11 flex-wrap items-center gap-2 py-1.5">
                          <span className="min-w-0 flex-1 truncate text-ui text-ink">
                            {m.name}
                            {m.appointment && <span className="text-ink-muted"> · {m.appointment}</span>}
                          </span>
                          {m.active === false && <Badge tone="warn">{t("Compte désactivé")}</Badge>}
                          {canShare ? (
                            <>
                              <Segmented
                                size="sm"
                                fill={false}
                                ariaLabel={t("Accès de {nom}", { nom: m.name })}
                                options={levelOptions}
                                value={m.level}
                                onChange={(level) =>
                                  change({ members: members.map((x) => (x.account_id === m.account_id ? { ...x, level } : x)) })
                                }
                              />
                              <IconButton
                                size="sm"
                                title={t("Retirer")}
                                aria-label={t("Retirer {nom}", { nom: m.name })}
                                onClick={() => change({ members: members.filter((x) => x.account_id !== m.account_id) })}
                              >
                                <X size={14} />
                              </IconButton>
                            </>
                          ) : (
                            <span className="text-meta text-ink-soft">{levelOptions.find((o) => o.value === m.level)?.label}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {canShare && (
                      <div className="flex items-center gap-2 pt-1">
                        <UserPlus size={14} className="shrink-0 text-ink-muted" />
                        <Select
                          className={inputClass}
                          value=""
                          placeholder={addable.length ? t("Ajouter un compte…") : t("Tous les comptes actifs y figurent")}
                          disabled={addable.length === 0}
                          aria-label={t("Ajouter un compte…")}
                          onChange={(e) => {
                            const account = addable.find((a) => a.id === e.target.value);
                            if (account)
                              change({
                                members: [
                                  ...members,
                                  { account_id: account.id, name: account.display_name, appointment: account.appointment, level: "reader" },
                                ],
                              });
                          }}
                        >
                          {addable.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.appointment ? `${a.display_name} · ${a.appointment}` : a.display_name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    )}
                  </div>
                  <p className="text-meta leading-snug text-ink-muted">
                    {t("Lecture seule : consulter, exporter, suivre la DIREX sans y écrire. Modification : tout, y compris jouer une cellule ou noter la 3A, sauf supprimer le dossier, le partager ou en changer le propriétaire.")}
                  </p>
                  {!canShare && (
                    <Callout tone="info">{t("Seul le propriétaire du dossier peut le supprimer, le partager ou en changer le propriétaire.")}</Callout>
                  )}
                </>
              )}
              <Presence show={Boolean(failure)}>
                {failure && (
                  <Callout tone="danger">
                    {failure instanceof ApiError ? failure.message : tServer("Action impossible pour l'instant. Réessayez.")}
                  </Callout>
                )}
              </Presence>
            </>
          )}
        </DialogBody>
      </Dialog>
      <Presence show={confirming}>
        {confirming && (
          <ConfirmDialog
            title={t("Transférer la propriété ?")}
            confirmLabel={t("Transférer")}
            tone="neutral"
            busy={transfer.isPending}
            onConfirm={() => transfer.mutate()}
            onCancel={() => setConfirming(false)}
          >
            {data?.owner
              ? t("{nom} deviendra propriétaire de « {dossier} ». Vous garderez l'accès en modification.", { nom: heirName, dossier: name })
              : t("Attribuer un propriétaire ne retire l'accès à personne : l'élément reste modifiable par toute l'unité, jusqu'à ce que son propriétaire en change le partage.")}
          </ConfirmDialog>
        )}
      </Presence>
    </>
  );
}
